/**
 * A resource's leave, days off and breaks — `monthlySchedule.timeOff` on the
 * resource, honoured by the server's availability (TimeOffPeriod.cs).
 *
 * Each entry is { start, end, from, to, reason }: every day from `start` to
 * `end` ("yyyy-mm-dd", inclusive), whole when `from`/`to` are null, otherwise
 * only between those two "HH:MM" times on each of those days. Dates are the
 * business's wall clock, like the weekly hours.
 *
 * The original month picker kept whole days in `daysOff`. The server still
 * honours those; here they are shown as entries, and written back as entries
 * the first time the calendar is changed.
 */

/**
 * A day off and a break. Leave is a day off that lasts longer, so it has no
 * label of its own; entries saved with the "vacation" label before it went are
 * read as days off.
 */
export const TIME_OFF_REASONS = ["dayOff", "break"];

/** The server's ceilings (TimeOffPeriod.MaxPeriods / MaxDays). */
export const MAX_TIME_OFF_PERIODS = 200;
export const MAX_TIME_OFF_DAYS = 366;

const pad = (value) => String(value).padStart(2, "0");

/** A local Date as "yyyy-mm-dd" — never via toISOString, which shifts to UTC. */
export const toIsoDate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** "yyyy-mm-dd" as a local Date at midnight. */
export const fromIsoDate = (iso) => {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
};

/** How many days an entry covers, both ends included. */
export const daysIn = ({ start, end }) =>
  Math.round((fromIsoDate(end) - fromIsoDate(start)) / 86400000) + 1;

const addDays = (iso, days) => {
  const date = fromIsoDate(iso);
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
};

/** The legacy day list as whole-day entries, consecutive days merged. */
const fromLegacyDays = (daysOff) =>
  [...new Set(daysOff || [])]
    .filter((day) => /^\d{4}-\d{2}-\d{2}$/.test(day))
    .sort()
    .reduce((entries, day) => {
      const last = entries[entries.length - 1];
      if (last && addDays(last.end, 1) === day) {
        last.end = day;
      } else {
        entries.push({ start: day, end: day, from: null, to: null, reason: "dayOff" });
      }
      return entries;
    }, []);

/** Every period on a resource's calendar, the legacy days included. */
export const timeOffOf = (monthlySchedule) => [
  ...fromLegacyDays(monthlySchedule?.daysOff),
  ...(monthlySchedule?.timeOff || []).map((entry) => ({
    start: entry.start,
    end: entry.end,
    from: entry.from || null,
    to: entry.to || null,
    reason: entry.reason === "break" ? "break" : "dayOff",
  })),
];

/** The calendar with `entries` as its periods, the legacy list emptied into them. */
export const withTimeOff = (monthlySchedule, entries) => ({
  ...(monthlySchedule || {}),
  daysOff: [],
  timeOff: entries,
});

/** The periods covering `iso`. */
export const entriesOn = (entries, iso) =>
  entries.filter((entry) => entry.start <= iso && iso <= entry.end);

/** The periods not yet over on `todayIso`, soonest first. */
export const upcoming = (entries, todayIso) =>
  entries
    .filter((entry) => entry.end >= todayIso)
    .sort(
      (a, b) =>
        a.start.localeCompare(b.start) || (a.from || "").localeCompare(b.from || ""),
    );

/** Whether two entries are the same period. */
export const sameEntry = (a, b) =>
  a.start === b.start &&
  a.end === b.end &&
  (a.from || null) === (b.from || null) &&
  (a.to || null) === (b.to || null) &&
  (a.reason || null) === (b.reason || null);
